import { useState, useRef } from 'react'
import Badge from 'react-bootstrap/Badge'
import Modal from 'react-bootstrap/Modal'
import { useNavigate } from 'react-router-dom'
import sha1 from 'sha1'
import { toastErrorLog } from '../../helpers/toastErrorLog'
import { useAuth } from '../../hooks/useAuth'
import createValidador from '../hooks/createValidador'
import ComponenteError from '../publicaciones/ComponenteError'
import { Checkbox } from '../reutilizables/checkbox/Checkbox'
import * as registroService from '../servicios/registroService'
import Input from '../ui/Input'
import LinkButton from '../ui/LinkButton'
import Upload from './Upload'

const PESO_POSICION_1 = 5
const PESO_POSICION_2 = 4
const PESO_POSICION_3 = 3
const PESO_POSICION_4 = 2
const PESO_POSICION_5 = 7
const PESO_POSICION_6 = 6
const PESO_POSICION_7 = 5
const PESO_POSICION_8 = 4
const PESO_POSICION_9 = 3
const PESO_POSICION_10 = 2
const MAX_CARACTERES = 150
const MAX_DNI = 20
const MAX_EMAIL = 50
const MAX_PASSWORD = 50
const MAX_TEL = 50
const MAX_CUIT = 11
const PASS_MIN_SEGURA = 10
const CUIT_EMPRESA = 2
const POSICION_2 = 2
const POSICION_3 = 3
const POSICION_4 = 4
const POSICION_5 = 5
const POSICION_6 = 6
const POSICION_7 = 7
const POSICION_8 = 8
const POSICION_9 = 9
const POSICION_10 = 10

const Registro = () => {
  const {
    loginDataContext: { nombre, empid, idUsuario },
    logout
  } = useAuth()
  const [showModal, setShowModal] = useState(false)
  const [regData, setRegData] = useState({
    cuit: '',
    nombre: '',
    apellido: '',
    dni: '',
    email: '',
    emailConfirmar: '',
    password: '',
    passwordConfirmar: '',
    telefono: '',
    celular: '',
    cargo: '',
    usuario: nombre,
    empid: empid
  })

  const [errorReg, setErrorReg] = useState('')
  const [errorCuit, setErrorCuit] = useState('1')
  const [showEstadoPassword, setShowEstadoPassword] = useState('')
  const [estadoPassword, setEstadoPassword] = useState('')
  const [textoPassword, setTextoPassword] = useState('')
  const [passwordValido, setPasswordValido] = useState(false)
  const [submiting, setSubmiting] = useState(false)
  const [chktyc, setChktyc] = useState(null)
  const [estadoImg, setEstadoImg] = useState({ frente: 0, dorso: 0 })
  const navigate = useNavigate()
  const tyc = useRef()

  const [errores, setErrores] = useState({})
  const { Validador } = createValidador()

  const esquema = {
    cuit: Validador.Kernel.string()
      .trim()
      .required()
      .max(MAX_CARACTERES)
      .label('CUIT/CUIL')
      .error((errors) => {
        return Validador.mensaje(errors)
      }),
    nombre: Validador.Kernel.string()
      .trim()
      .required()
      .max(MAX_CARACTERES)
      .label('Nombre')
      .error((errors) => {
        return Validador.mensaje(errors)
      }),
    apellido: Validador.Kernel.string()
      .trim()
      .required()
      .max(MAX_CARACTERES)
      .label('Apellido')
      .error((errors) => {
        return Validador.mensaje(errors)
      }),
    dni: Validador.Kernel.string()
      .trim()
      .required()
      .max(MAX_DNI)
      .label('DNI')
      .error((errors) => {
        return Validador.mensaje(errors)
      }),
    email: Validador.Kernel.string()
      .trim()
      .required()
      .max(MAX_EMAIL)
      .email()
      .label('Email')
      .error((errors) => {
        return Validador.mensaje(errors)
      }),
    emailConfirmar: Validador.Kernel.string()
      .trim()
      .required()
      .email()
      .max(MAX_EMAIL)
      .label('Email confirmar')
      .valid(Validador.Kernel.ref('email'))
      .error((e) => {
        return Validador.mensaje(e, {
          'any.allowOnly': <>El email ingresado no coincide</>
        })
      }),
    password: Validador.Kernel.string()
      .trim()
      .required()
      .max(MAX_PASSWORD)
      .label('Password')
      .error((errors) => {
        return Validador.mensaje(errors)
      }),
    passwordConfirmar: Validador.Kernel.string()
      .trim()
      .required()
      .max(MAX_PASSWORD)
      .label('Password confirmar')
      .valid(Validador.Kernel.ref('password'))
      .error((e) => {
        return Validador.mensaje(e, {
          'any.allowOnly': <>El password ingresado no coincide</>
        })
      }),
    telefono: Validador.Kernel.string()
      .trim()
      .required()
      .max(MAX_TEL)
      .label('Teléfono')
      .error((errors) => {
        return Validador.mensaje(errors)
      }),
    tyc: Validador.Kernel.any()
      .empty(null)
      .required()
      .label('TyC')
      .error((e) => {
        return Validador.mensaje(e, {
          'any.required': <>Para continuar debés aceptar los Términos y Condiciones de eBuyMarket</>
        })
      })
  }

  const handleChange = (e) => {
    // TODO: SEGURIDAD - SHA1 es inseguro para passwords
    // El backend actualmente requiere SHA1. Se debe migrar a bcrypt/argon2
    // y mover el hashing al backend. Issue: [crear issue en GitHub]
    setRegData({
      ...regData,
      [e.target.name]:
        e.target.name === 'password' || e.target.name === 'passwordConfirmar'
          ? sha1(e.target.value) // nosemgrep: javascript.lang.security.insecure-hash-algorithm-sha1
          : e.target.value
    })
    e.target.name === 'password' && showNivelPassword(e)
  }

  const showNivelPassword = (e) => {
    const exp1 = /[a-z]/
    const exp2 = /[0-9]/
    const exp3 = /[A-Z]/
    const valor1 = exp1.test(e.target.value)
    const valor2 = exp2.test(e.target.value)
    const valor3 = exp3.test(e.target.value)

    if (valor1 || valor2 || valor3) {
      setEstadoPassword('badge badge-danger')
      setTextoPassword('Poco segura')
      setPasswordValido(false)
    }
    if (
      (valor1 && !valor2 && valor3) ||
      (valor1 && valor2 && !valor3) ||
      (!valor1 && valor2 && valor3)
    ) {
      setEstadoPassword('badge badge-warning')
      setTextoPassword('Seguridad media')
      setPasswordValido(true)
    }
    if (valor1 && valor2 && valor3) {
      setEstadoPassword('badge badge-success')
      setTextoPassword('Muy segura')
      setPasswordValido(true)
    }
    if (e.target.value.length < PASS_MIN_SEGURA) {
      setEstadoPassword('badge badge-danger')
      setTextoPassword('Poco segura')
      setPasswordValido(false)
    }
    setShowEstadoPassword(true)
  }

  const valido = (name) => {
    const error = Validador.validarDato(
      esquema,
      {
        cuit: regData.cuit,
        nombre: regData.nombre,
        apellido: regData.apellido,
        dni: regData.dni,
        email: regData.email,
        emailConfirmar: regData.emailConfirmar,
        password: regData.password,
        passwordConfirmar: regData.passwordConfirmar,
        telefono: regData.telefono,
        tyc: chktyc
      },
      name
    )
    setErrores(error || {})
    if (error) {
      return false
    } else {
      return true
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (
      valido('cuit') &&
      valido('nombre') &&
      valido('apellido') &&
      valido('dni') &&
      valido('email') &&
      valido('emailConfirmar') &&
      valido('password') &&
      valido('passwordConfirmar') &&
      valido('telefono') &&
      valido('tyc') &&
      passwordValido &&
      errorCuit === null &&
      estadoImg.frente === 1 &&
      estadoImg.dorso === 1
    ) {
      submit()
    } else if (errorCuit !== null) {
      setErrores({ cuit: 'El CUIT/CUIL ingresado no es válido.' })
    } else if (estadoImg.frente !== 1) {
      setErrores({
        frenteDNI: 'Tenés que cargar la foto del frente de tu DNI'
      })
    } else if (estadoImg.dorso !== 1) {
      setErrores({ dorsoDNI: 'Tenés que cargar la foto del dorso de tu DNI' })
    }
  }

  const submit = async () => {
    try {
      ///Envío en SP email a usuario HELPDESK_EBM
      setSubmiting(true)
      const { data } = await registroService.registrarUsuario(regData)
      if (data.estado) {
        setShowModal(true)
      } else {
        setErrorReg(true)

        setSubmiting(false)
      }
    } catch (error) {
      const mensaje = `Ocurrió un error al registar usuario.`
      toastErrorLog(error, mensaje, 'registro')
      setSubmiting(false)
    }
  }

  const validarCUIT = async (cuit) => {
    try {
      const { data } = await registroService.validarCUIT(cuit)
      if (data.registrado === 1) {
        setErrorCuit(1)
        setErrores({ cuit: 'El CUIT ingresado ya existe' })
      } else if (data.registrado === CUIT_EMPRESA) {
        setErrorCuit(1)
        setErrores({ cuit: 'No podés usar el CUIT de una empresa' })
      } else {
        if (!isCuit(regData.cuit) && regData.cuit !== '') {
          setErrorCuit(1)
          setErrores({ cuit: 'El CUIT/CUIL ingresado no es válido.' })
        } else {
          setErrorCuit(null)
        }
      }
    } catch (error) {
      const mensaje = `Ocurrió un error al valiar CUIT.`
      toastErrorLog(error, mensaje, 'registro')
    }
  }

  const handleBlur = (e) => {
    valido(e.target.name)
    if (e.target.name === 'cuit') {
      regData.cuit !== '' ? validarCUIT(regData.cuit) : setErrorCuit(1)
    }
  }

  const handleClose = () => {
    logout()
    setShowModal(false)
    navigate('/Login')
  }

  const isCuit = (cuit) => {
    if (cuit.length < MAX_CUIT) {
      return false
    } else {
      const ax = cuit.substr(POSICION_10, 1)
      const bx = cuit.substr(POSICION_9, 1) * PESO_POSICION_10
      const cx = cuit.substr(POSICION_8, 1) * PESO_POSICION_9
      const dx = cuit.substr(POSICION_7, 1) * PESO_POSICION_8
      const ex = cuit.substr(POSICION_6, 1) * PESO_POSICION_7
      const fx = cuit.substr(POSICION_5, 1) * PESO_POSICION_6
      const gx = cuit.substr(POSICION_4, 1) * PESO_POSICION_5
      const hx = cuit.substr(POSICION_3, 1) * PESO_POSICION_4
      const ix = cuit.substr(POSICION_2, 1) * PESO_POSICION_3
      const jx = cuit.substr(1, 1) * PESO_POSICION_2
      const kx = cuit.substr(0, 1) * PESO_POSICION_1
      const rx =
        Number(ax) +
        Number(bx) +
        Number(cx) +
        Number(dx) +
        Number(ex) +
        Number(fx) +
        Number(gx) +
        Number(hx) +
        Number(ix) +
        Number(jx) +
        Number(kx)
      const resx = rx % MAX_CUIT
      if (resx === 0) {
        return true
      } else {
        return false
      }
    }
  }
  const imgCheck = (imageTipo) => {
    imageTipo === 1
      ? setEstadoImg({ ...estadoImg, frente: 1 })
      : setEstadoImg({ ...estadoImg, dorso: 1 })
  }

  return (
    <>
      <br />
      <div className="row">
        <div className="col">
          <h3>¿Querés registrarte en forma particular?</h3>
          <p>
            En este sitio, puedes acceder a nombre de una empresa, con lo cual estarias comprando
            y/o vendiendo productos para esa empresa para la cual trabajas; como asi tambien, puedes
            ingresar de forma personal, como usuario particular; por ende, estarias en la libertad
            de comprar y vender productos personales que nada tienen que ver con la empresa.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col text-center">
            <br />
            <Input
              type="text"
              name="cuit"
              onChange={(e) => handleChange(e)}
              onBlur={(e) => handleBlur(e)}
              className="w-80 text-area"
              placeholder="CUIT/CUIL Propio (sin espacios ni guiones)"
              errores={errores}
            />
            &nbsp;
            <span>
              <i
                className={errorCuit === null && regData.cuit !== '' ? 'fa fa-check-circle' : ''}
                aria-hidden="true"
              />
            </span>
            <br />
            <Input
              type="text"
              name="nombre"
              className="w-80 text-area"
              placeholder="Nombre"
              onChange={handleChange}
              onBlur={(e) => handleBlur(e)}
              errores={errores}
            />
            <Input
              type="text"
              className="w-80 text-area"
              placeholder="Apellido"
              name="apellido"
              onChange={handleChange}
              onBlur={(e) => handleBlur(e)}
              errores={errores}
            />
            <Input
              type="text"
              className="w-80 text-area"
              placeholder="Documento de Identidad"
              name="dni"
              onChange={handleChange}
              onBlur={(e) => handleBlur(e)}
              errores={errores}
            />
            <Input
              type="email"
              className="w-80 text-area"
              placeholder="Direccion de email"
              name="email"
              onChange={handleChange}
              onBlur={(e) => handleBlur(e)}
              errores={errores}
            />
            <Input
              type="email"
              className="w-80 text-area"
              placeholder="Repetir direccion de email"
              name="emailConfirmar"
              onChange={handleChange}
              onBlur={(e) => handleBlur(e)}
              errores={errores}
            />
            <br />
            <p>Agregá frente DNI</p>
            <Upload
              name="frenteDNI"
              accept={'image/*'}
              idUsuario={idUsuario}
              idTipo={1}
              onCheck={imgCheck}
              errores={errores}
            />
          </div>
          <div className="col text-center">
            <br />
            <input
              type="text"
              className="w-80 text-area"
              placeholder=""
              name="empid"
              disabled="disabled"
              value={`Usuario conectado: ${nombre}`}
            />
            <Input
              type="password"
              className="w-80 text-area"
              placeholder="Contraseña"
              name="password"
              onChange={handleChange}
              onBlur={(e) => handleBlur(e)}
              errores={errores}
            />
            <h4>{showEstadoPassword && <Badge variant={estadoPassword}>{textoPassword}</Badge>}</h4>
            <Input
              type="password"
              className="w-80 text-area"
              placeholder="Repetir Contraseña"
              name="passwordConfirmar"
              onChange={handleChange}
              onBlur={(e) => handleBlur(e)}
              errores={errores}
            />
            <Input
              type="text"
              className="w-80 text-area"
              placeholder="Telefono"
              name="telefono"
              onChange={handleChange}
              onBlur={(e) => handleBlur(e)}
              errores={errores}
            />
            <input
              type="text"
              className="w-80 text-area"
              placeholder="Celular"
              name="celular"
              onChange={handleChange}
            />
            <input
              type="text"
              className="w-80 text-area"
              placeholder="Cargo en la Empresa"
              name="cargo"
              onChange={handleChange}
            />

            <p>Agregá dorso DNI</p>
            <Upload
              name="dorsoDNI"
              accept={'image/*'}
              idUsuario={idUsuario}
              idTipo={2}
              onCheck={imgCheck}
              errores={errores}
            />
          </div>
        </div>

        {errorReg && (
          <div className="row">
            <div className="col">
              <ComponenteError tipo="E" idAviso={12} />
            </div>
          </div>
        )}

        <div className="row">
          <div className="col text-center">
            <br />
            He leído y acepto los{' '}
            <a href={''} target="_blank" rel="noreferrer">
              T&eacute;rminos y Condiciones de eBuyMarket
            </a>{' '}
            <br />
            Aceptar&nbsp;
            <Checkbox
              type="checkbox"
              name="tyc"
              required
              ref={tyc}
              onClick={() => setChktyc(tyc.current.checked ? 'on' : null)}
              errores={errores}
            />
            <br />
            <br />
            <button
              type="submit"
              className="btn btn-sign-in w-10"
              onClick={(e) => handleSubmit(e)}
              disabled={submiting && 'disabled'}
            >
              <i className="fa fa-sign-in" aria-hidden="true" />
              &nbsp; {submiting ? 'Aguarde un momento..' : 'Registrarme!'}
            </button>
          </div>
        </div>
      </form>
      <Modal show={showModal} onHide={handleClose} size={'lg'} animation={false}>
        <Modal.Header closeButton>
          <Modal.Title>
            <h3>¡Tu registración se ha completado!</h3>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Queda el último paso para validar tus datos.
          <br />
          Te enviamos por correo un link para que verifiques la cuenta, una vez finalizado el
          proceso de verificación, podrás iniciar sesión en e-BuyMarket con tu cuenta de usuario
          Particular
          <br />
          Muchas gracias
          <br />
          El equipo de e-buyMarket
        </Modal.Body>
        <Modal.Footer>
          <LinkButton className="btn btn-sign-in" onClick={() => handleClose()}>
            <i className="fa fa-sign-in" aria-hidden="true" /> Iniciar Sesión
          </LinkButton>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default Registro
